import {groq} from 'next-sanity';

import {DOCUMENT_TYPE_ARTICLE_FRONTPAGE} from '@/modules/article/sanity/frontpage/constants';
import {articleInCardFragmentToArticleDetails} from '@/modules/article/sanity/query';
import {DOCUMENT_TYPE_ABOUT_FRONTPAGE} from '@/modules/finkn/about/frontpage/sanity/constants';
import {DOCUMENT_TYPE_COMPANIES_FRONTPAGE} from '@/modules/finkn/companies/sanity/frontpage/constants';
import {DOCUMENT_TYPE_FAQ_FRONTPAGE} from '@/modules/finkn/faq/sanity/frontpage/constants';
import {DOCUMENT_TYPE_BANK_AREA} from '@/modules/finkn/field-of-area/bank/area/sanity/constants';
import {DOCUMENT_TYPE_BANK_FRONTPAGE} from '@/modules/finkn/field-of-area/bank/frontpage/sanity/constants';
import {DOCUMENT_TYPE_BANK_GUIDEBOOK} from '@/modules/finkn/field-of-area/bank/guidebook/sanity/constants';
import {DOCUMENT_TYPE_COLLECTION_AREA} from '@/modules/finkn/field-of-area/collection/area/sanity/constants';
import {DOCUMENT_TYPE_COLLECTION_FRONTPAGE} from '@/modules/finkn/field-of-area/collection/frontpage/sanity/constants';
import {DOCUMENT_TYPE_COLLECTION_GUIDEBOOK} from '@/modules/finkn/field-of-area/collection/guidebook/sanity/constants';
import {DOCUMENT_TYPE_INSURANCE_AREA} from '@/modules/finkn/field-of-area/insurance/area/sanity/constants';
import {DOCUMENT_TYPE_INSURANCE_FRONTPAGE} from '@/modules/finkn/field-of-area/insurance/frontpage/sanity/constants';
import {DOCUMENT_TYPE_INSURANCE_GUIDEBOOK} from '@/modules/finkn/field-of-area/insurance/guidebook/sanity/constants';
import {DOCUMENT_TYPE_FINANCE_FRONTPAGE} from '@/modules/finkn/finance/frontpage/sanity/constants';
import {DOCUMENT_TYPE_FOR_LAWYERS_FRONTPAGE} from '@/modules/finkn/for-lawyers/frontpage/sanity/constants';
import {DOCUMENT_TYPE_LOGIN_FRONTPAGE} from '@/modules/finkn/login/sanity/constants';
import {DOCUMENT_TYPE_NEMND} from '@/modules/finkn/nemnd/sanity/constants';
import {internalLinkToHref, linkFragmentToHref} from '@/modules/foundation/sanity/link/query';
import type {KeyedArray} from '@/modules/foundation/shared/types';
import type {NavbarQueryResult, SiteSettingsQueryResult} from '@/sanity/schemas/types';

import type {NavGroupLink, Navigation, NavItem} from '../../components/Navbar';
import {DOCUMENT_TYPE_SITE_SETTINGS} from '../constants';

const NAVBAR_FRAGMENT = `//groq
	"insurance": {
		"frontpage": *[_type == "${DOCUMENT_TYPE_INSURANCE_FRONTPAGE}"][0] {
			title
		},
		"guidebook": *[_type == "${DOCUMENT_TYPE_INSURANCE_GUIDEBOOK}"][0] {
			title
		},
		"areas": *[_type == "${DOCUMENT_TYPE_INSURANCE_AREA}"] | order(orderRank) {
			"slug": slug.current,
			title,
			_id
		},
		"nemnds": *[_type == "${DOCUMENT_TYPE_NEMND}" && fieldOfArea._ref == "singleton-insurance-frontpage"] {
			"slug": slug.current,
			name,
			_id
		}
	},
	"bank": {
		"frontpage": *[_type == "${DOCUMENT_TYPE_BANK_FRONTPAGE}"][0] {
			title
		},
		"guidebook": *[_type == "${DOCUMENT_TYPE_BANK_GUIDEBOOK}"][0] {
			title
		},
		"areas": *[_type == "${DOCUMENT_TYPE_BANK_AREA}"] | order(orderRank) {
			"slug": slug.current,
			title,
			_id
		},
		"nemnds": *[_type == "${DOCUMENT_TYPE_NEMND}" && fieldOfArea._ref == "singleton-bank-frontpage"] {
			"slug": slug.current,
			name,
			_id
		}
	},
	"collection": {
		"frontpage": *[_type == "${DOCUMENT_TYPE_COLLECTION_FRONTPAGE}"][0] {
			title
		},
		"guidebook": *[_type == "${DOCUMENT_TYPE_COLLECTION_GUIDEBOOK}"][0] {
			title
		},
		"areas": *[_type == "${DOCUMENT_TYPE_COLLECTION_AREA}"] | order(orderRank) {
			"slug": slug.current,
			title,
			_id
		},
		"nemnds": *[_type == "${DOCUMENT_TYPE_NEMND}" && fieldOfArea._ref == "singleton-collection-frontpage"] {
			"slug": slug.current,
			name,
			_id
		}
	},
	"about": *[_type == "${DOCUMENT_TYPE_ABOUT_FRONTPAGE}"][0] {
		title
	},
	"nemndspraksisLink": *[_type == "${DOCUMENT_TYPE_SITE_SETTINGS}"][0].nemndspraksisLink,
	"financeFrontpageTitle": *[_type == "${DOCUMENT_TYPE_FINANCE_FRONTPAGE}"][0].title,
	"loginFrontpageTitle": *[_type == "${DOCUMENT_TYPE_LOGIN_FRONTPAGE}"][0].title,
	"forLawyersFrontpageTitle": *[_type == "${DOCUMENT_TYPE_FOR_LAWYERS_FRONTPAGE}"][0].title
`;

export const navbarQuery = groq`{
	${NAVBAR_FRAGMENT}
}`;

export const navigationFromNavbarFragment = (
	data: NavbarQueryResult,
	siteSettings: SiteSettingsQueryResult,
): Navigation => {
	const {bank, insurance, collection} = siteSettings ?? {};

	const extraInsuranceLinks: KeyedArray<NavGroupLink> = insurance?.resourceLinks?.length
		? insurance.resourceLinks.map((link) => ({
				_key: link._key,
				title: link.title ?? '',
				href: linkFragmentToHref(link),
			}))
		: [];

	const extraBankLinks: KeyedArray<NavGroupLink> = bank?.resourceLinks?.length
		? bank.resourceLinks.map((link) => ({
				_key: link._key,
				title: link.title ?? '',
				href: linkFragmentToHref(link),
			}))
		: [];

	const extraCollectionLinks: KeyedArray<NavGroupLink> = collection?.resourceLinks?.length
		? collection.resourceLinks.map((link) => ({
				_key: link._key,
				title: link.title ?? '',
				href: linkFragmentToHref(link),
			}))
		: [];

	const insuranceNav: NavItem = {
		title: data.insurance?.frontpage?.title ?? '',
		href: internalLinkToHref(DOCUMENT_TYPE_INSURANCE_FRONTPAGE),
		areas: {
			links:
				data.insurance?.areas?.map((area) => ({
					title: area.title ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_INSURANCE_AREA, area.slug ?? ''),
					_key: area._id,
				})) ?? [],
		},
		resources: {
			links: [
				{
					_key: 'guidebook',
					title: data.insurance?.guidebook?.title ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_INSURANCE_GUIDEBOOK),
				},
				{
					_key: '2349sdfsd9',
					title: 'Vanlige spørsmål om forsikring',
					href: `${internalLinkToHref(DOCUMENT_TYPE_FAQ_FRONTPAGE)}?fieldOfArea=singleton-insurance-frontpage`,
				},
				{
					_key: '2sdfs2344d',
					title: 'Foretakene du kan klage på',
					href: `${internalLinkToHref(DOCUMENT_TYPE_COMPANIES_FRONTPAGE)}?fieldOfArea=forsikring`,
				},
				{
					_key: '2349sdfs345d',
					title: 'Nyheter om forsikring',
					href: `${internalLinkToHref(DOCUMENT_TYPE_ARTICLE_FRONTPAGE)}?fieldOfArea=singleton-insurance-frontpage`,
				},
				...extraInsuranceLinks,
			],
		},
		nemnd: {
			links:
				data.insurance?.nemnds?.map((nemnd) => ({
					title: nemnd.name ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_NEMND, nemnd.slug ?? ''),
					_key: nemnd._id,
				})) ?? [],
		},
		articles: insurance?.importantArticles?.map((article) => ({
			...articleInCardFragmentToArticleDetails(article),
			_key: article._id,
		})),
	};

	const bankNav: NavItem = {
		title: data.bank?.frontpage?.title ?? '',
		href: internalLinkToHref(DOCUMENT_TYPE_BANK_FRONTPAGE),
		areas: {
			links:
				data.bank?.areas?.map((area) => ({
					title: area.title ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_BANK_AREA, area.slug ?? ''),
					_key: area._id,
				})) ?? [],
		},
		resources: {
			links: [
				{
					_key: 'guidebook',
					title: data.bank?.guidebook?.title ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_BANK_GUIDEBOOK),
				},
				{
					_key: '2349sdfsd9',
					title: 'Vanlige spørsmål om bank',
					href: `${internalLinkToHref(DOCUMENT_TYPE_FAQ_FRONTPAGE)}?fieldOfArea=singleton-bank-frontpage`,
				},
				{
					_key: '2sdfs2344d',
					title: 'Foretakene du kan klage på',
					href: `${internalLinkToHref(DOCUMENT_TYPE_COMPANIES_FRONTPAGE)}?fieldOfArea=bank`,
				},
				{
					_key: '2349sdfs345d',
					title: 'Nyheter om bank',
					href: `${internalLinkToHref(DOCUMENT_TYPE_ARTICLE_FRONTPAGE)}?fieldOfArea=singleton-bank-frontpage`,
				},
				...extraBankLinks,
			],
		},
		nemnd: {
			links:
				data.bank?.nemnds?.map((nemnd) => ({
					title: nemnd.name ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_NEMND, nemnd.slug ?? ''),
					_key: nemnd._id,
				})) ?? [],
		},
		articles: bank?.importantArticles?.map((article) => ({
			...articleInCardFragmentToArticleDetails(article),
			_key: article._id,
		})),
	};

	const collectionNav: NavItem = {
		title: data.collection?.frontpage?.title ?? '',
		href: internalLinkToHref(DOCUMENT_TYPE_COLLECTION_FRONTPAGE),
		areas: {
			links:
				data.collection?.areas?.map((area) => ({
					title: area.title ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_COLLECTION_AREA, area.slug ?? ''),
					_key: area._id,
				})) ?? [],
		},
		resources: {
			links: [
				{
					_key: 'guidebook',
					title: data.collection?.guidebook?.title ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_COLLECTION_GUIDEBOOK),
				},
				{
					_key: '2349sdfsd9',
					title: 'Vanlige spørsmål om inkasso',
					href: `${internalLinkToHref(DOCUMENT_TYPE_FAQ_FRONTPAGE)}?fieldOfArea=singleton-collection-frontpage`,
				},
				{
					_key: '2sdfs2344d',
					title: 'Foretakene du kan klage på',
					href: `${internalLinkToHref(DOCUMENT_TYPE_COMPANIES_FRONTPAGE)}?fieldOfArea=inkasso`,
				},
				{
					_key: '2349sdfs345d',
					title: 'Nyheter om inkasso',
					href: `${internalLinkToHref(DOCUMENT_TYPE_ARTICLE_FRONTPAGE)}?fieldOfArea=singleton-collection-frontpage`,
				},
				...extraCollectionLinks,
			],
		},
		nemnd: {
			links:
				data.collection?.nemnds?.map((nemnd) => ({
					title: nemnd.name ?? '',
					href: internalLinkToHref(DOCUMENT_TYPE_NEMND, nemnd.slug ?? ''),
					_key: nemnd._id,
				})) ?? [],
		},
		articles: collection?.importantArticles?.map((article) => ({
			...articleInCardFragmentToArticleDetails(article),
			_key: article._id,
		})),
	};

	const about = {
		title: data.about?.title ?? 'Om oss',
	};

	return {
		bank: bankNav,
		collection: collectionNav,
		insurance: insuranceNav,
		about,
		nemndspraksisLink: data.nemndspraksisLink ?? '',
		financeFrontpageTitle: data.financeFrontpageTitle ?? 'For finansforetak',
		loginFrontpageTitle: data.loginFrontpageTitle ?? 'Logg inn',
		forLawyersFrontpageTitle: data.forLawyersFrontpageTitle ?? 'For advokater',
	};
};
